import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { type AuthUser } from "wasp/auth";
import {
  getAdminFaqItems,
  getAdminFaqCategories,
  updateFaqItem,
  deleteFaqItem,
  useQuery,
  useAction,
} from "wasp/client/operations";
import DefaultLayout from "../../layout/DefaultLayout";
import Breadcrumb from "../../layout/Breadcrumb";
import useTranslation from "../../../client/hooks/useTranslation";
import { useToast } from "../../../client/hooks/use-toast";
import { Button } from "../../../client/components/ui/button";

interface ItemRow {
  id: string;
  slug: string;
  questionFr: string;
  isPublished: boolean;
  sortOrder: number;
  category?: { id: string; nameFr: string };
}

interface CatOption {
  id: string;
  nameFr: string;
}

export default function AdminHelpPage({ user }: { user: AuthUser }) {
  const t = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [categoryId, setCategoryId] = useState<string>("");
  const [status, setStatus] = useState<"ALL" | "PUBLISHED" | "DRAFT">("ALL");

  const { data: cats } = useQuery(getAdminFaqCategories, undefined);
  const { data, refetch } = useQuery(getAdminFaqItems, {
    categoryId: categoryId || undefined,
    status,
  });
  const items: ItemRow[] = (data as ItemRow[]) ?? [];
  const categories: CatOption[] = (cats as CatOption[]) ?? [];

  const updateItem = useAction(updateFaqItem);
  const deleteItem = useAction(deleteFaqItem);

  const togglePublish = async (it: ItemRow) => {
    await updateItem({ id: it.id, isPublished: !it.isPublished });
    refetch();
  };

  const remove = async (id: string) => {
    if (!window.confirm(t.adminHelp.confirmDelete)) return;
    await deleteItem({ id });
    toast({ title: t.adminHelp.deleted });
    refetch();
  };

  return (
    <DefaultLayout user={user}>
      <Breadcrumb pageName={t.adminHelp.questionsTitle} />

      <div className="mx-auto max-w-5xl">
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-xl font-bold text-foreground">{t.adminHelp.questionsTitle}</h2>
          <div className="flex gap-2">
            <Button asChild variant="outline">
              <Link to="/admin/help">{t.adminHelp.manageCategories}</Link>
            </Button>
            <Button onClick={() => navigate("/admin/help/new")}>{t.adminHelp.newQuestion}</Button>
          </div>
        </div>

        <div className="mb-4 flex flex-wrap gap-3">
          <select
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
            className="rounded border border-border bg-card px-3 py-2 text-sm text-foreground"
          >
            <option value="">{t.adminHelp.filterAllCategories}</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nameFr}
              </option>
            ))}
          </select>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as "ALL" | "PUBLISHED" | "DRAFT")}
            className="rounded border border-border bg-card px-3 py-2 text-sm text-foreground"
          >
            <option value="ALL">{t.adminHelp.filterAllStatuses}</option>
            <option value="PUBLISHED">{t.adminHelp.published}</option>
            <option value="DRAFT">{t.adminHelp.draft}</option>
          </select>
        </div>

        <ul className="divide-y divide-border rounded-lg border border-border bg-card">
          {items.map((it) => (
            <li key={it.id} className="flex items-center justify-between gap-3 p-4">
              <div className="min-w-0">
                <p className="truncate font-medium text-foreground">{it.questionFr}</p>
                <p className="text-sm text-muted-foreground">
                  {it.category?.nameFr ?? "—"} · #{it.sortOrder} ·{" "}
                  <span className={it.isPublished ? "text-primary" : "text-muted-foreground"}>
                    {it.isPublished ? t.adminHelp.published : t.adminHelp.draft}
                  </span>
                </p>
              </div>
              <div className="flex shrink-0 gap-2">
                <Button size="sm" variant="outline" onClick={() => navigate(`/admin/help/${it.id}/edit`)}>
                  {t.adminHelp.edit}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => togglePublish(it)}>
                  {it.isPublished ? t.adminHelp.unpublish : t.adminHelp.publish}
                </Button>
                <Button size="sm" variant="destructive" onClick={() => remove(it.id)}>
                  {t.adminHelp.delete}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </DefaultLayout>
  );
}
